import type { AxiosInstance } from 'axios'
import { Platform } from 'react-native'
import apiClient from '../API/apiClient'
import { normalizeMessage, normalizeUnreadPreview } from './messageHelpers'
import { SupportAttachmentReadError } from './supportErrors'
import type { QuickReplyId } from './quickReplies'
import type {
  PendingAttachment,
  SupportAction,
  SupportConversation,
  SupportMessage,
  SupportUnreadSummary,
} from './types'

export interface SendMessageInput {
  body: string
  attachments?: PendingAttachment[]
  /** Idempotency key so the optimistic bubble can be reconciled with the realtime refetch (DR-1). */
  clientId?: string
}

const toConversation = (d: Record<string, unknown>): SupportConversation => ({
  id: String(d.id),
  status: (d.status ?? 'open') as SupportConversation['status'],
  channel: String(d.channel ?? 'in_app'),
  lastMessageAt: (d.lastMessageAt ?? d.last_message_at ?? null) as string | null,
  createdAt: String(d.createdAt ?? d.created_at ?? ''),
})

/**
 * ATL-2066 — the bytes of a web attachment, as a Blob a browser FormData will send as a file.
 *
 * The pickers' web builds hand over the user's original `File` (`asset.file`); that is used as is.
 * Without it (expo-image-picker drops `file` when the browser cannot decode the image) the asset's
 * `data:`/`blob:` uri is read back with fetch. A Blob whose type is not the picker's MIME type is
 * re-wrapped with it — the same type the native path declares — because the server accepts a file
 * by its declared type, and a Blob read back from a uri may come out typeless or as text/plain.
 *
 * Anything else throws: an attachment that cannot be read must fail the send visibly, never
 * reach the server as a message without its file. The error is typed (ATL-2068) so the chat can
 * ask the merchant to attach the file again rather than to retry.
 */
const webAttachmentBlob = async (att: PendingAttachment): Promise<Blob> => {
  let blob: Blob | undefined = att.file
  if (!blob) {
    if (!/^(data|blob):/.test(att.uri)) throw new SupportAttachmentReadError(att.name)
    const res = await fetch(att.uri)
    if (!res.ok) throw new SupportAttachmentReadError(att.name)
    blob = await res.blob()
  }
  return !att.type || blob.type === att.type ? blob : new Blob([blob], { type: att.type })
}

export const createSupportApi = (http: AxiosInstance) => ({
  async getOrCreateConversation(): Promise<SupportConversation> {
    const { data } = await http.post('/support/conversations', {})
    return toConversation(data as Record<string, unknown>)
  },

  async getMessages(
    conversationId: string,
    opts: { before?: string; limit?: number } = {}
  ): Promise<SupportMessage[]> {
    const { data } = await http.get(`/support/conversations/${conversationId}/messages`, { params: opts })
    const list = Array.isArray((data as { messages?: unknown })?.messages)
      ? (data as { messages: Record<string, unknown>[] }).messages
      : []
    return list.map(normalizeMessage)
  },

  async sendMessage(conversationId: string, input: SendMessageInput): Promise<SupportMessage> {
    const url = `/support/conversations/${conversationId}/messages`
    let data: unknown
    if (input.attachments && input.attachments.length > 0) {
      const form = new FormData()
      form.append('body', input.body)
      if (input.clientId) form.append('clientId', input.clientId)
      // Do NOT set Content-Type manually — let the platform add the multipart boundary (ER-2).
      for (const att of input.attachments) {
        if (Platform.OS === 'web') {
          // ATL-2066: a browser FormData only sends a Blob as a file part; any other value is
          // stringified ("[object Object]") and the file never leaves the page.
          form.append('files', await webAttachmentBlob(att), att.name)
        } else {
          // RN's FormData reads `{ uri, name, type }` as a file part and streams the uri.
          form.append('files', { uri: att.uri, name: att.name, type: att.type } as unknown as Blob)
        }
      }
      const res = await http.post(url, form)
      data = res.data
    } else {
      const res = await http.post(url, { body: input.body, clientId: input.clientId })
      data = res.data
    }
    const payload = (data as { message?: Record<string, unknown> })?.message ?? data
    return normalizeMessage(payload as Record<string, unknown>)
  },

  /**
   * The badge count, plus (ATL-1343) a preview of the newest unread message for the in-app
   * toast. A backend that predates the preview simply omits it and the caller sees null.
   */
  async getUnread(): Promise<SupportUnreadSummary> {
    const { data } = await http.get('/support/unread')
    const d = (data ?? {}) as {
      conversationId?: string | null
      unreadCount?: number
      latestMessage?: unknown
    }
    return {
      conversationId: d.conversationId ?? null,
      unreadCount: Number(d.unreadCount ?? 0),
      latestMessage: normalizeUnreadPreview(d.latestMessage),
    }
  },

  async markRead(conversationId: string): Promise<void> {
    await http.post(`/support/conversations/${conversationId}/read`, {})
  },

  // ── ATL-1323: support agent proposals ────────────────────────────────────────
  async getAction(actionId: string): Promise<SupportAction> {
    const { data } = await http.get(`/support/actions/${encodeURIComponent(actionId)}`)
    return toAction(data)
  },

  /**
   * Tell the backend that drafted item `index` was saved as the product `firestoreId`. The backend
   * verifies the product in Firestore before recording it, so this cannot mark anything saved that
   * is not.
   */
  async reportDraftItemApplied(actionId: string, index: number, firestoreId: string): Promise<SupportAction> {
    const { data } = await http.post(
      `/support/actions/${encodeURIComponent(actionId)}/items/${encodeURIComponent(String(index))}/applied`,
      { firestoreId }
    )
    return toAction(data)
  },

  async dismissAction(actionId: string): Promise<SupportAction> {
    const { data } = await http.post(`/support/actions/${encodeURIComponent(actionId)}/dismiss`, {})
    return toAction(data)
  },

  /**
   * ACT-QUICK-REPLY-001 — report a tap on a welcome quick-reply button. The backend takes the
   * organization and user from the token and proves the message is this user's own welcome.
   */
  async recordWelcomeQuickReplyTap(messageId: string, reply: QuickReplyId): Promise<string> {
    const { data } = await http.post('/onboarding/welcome-quick-reply-taps', { messageId, reply })
    return String((data as { outcome?: unknown } | null)?.outcome ?? '')
  },
})

/**
 * The backend wraps an action as `{ action }`. A body without one is a contract break, and rendering
 * it as an empty card would hide that, so it throws.
 */
const toAction = (data: unknown): SupportAction => {
  const action = (data as { action?: SupportAction } | null)?.action
  if (!action || typeof action.id !== 'string' || !Array.isArray(action.items)) {
    throw new Error('support action response is malformed')
  }
  return action
}

/** Production instance bound to the shared authenticated axios client. */
export const supportApi = createSupportApi(apiClient)
export type SupportApi = ReturnType<typeof createSupportApi>
